import { useEffect, useRef, type RefObject } from 'react';
import { useReducedMotion } from 'motion/react';

const SCROLL_ACCUM_THRESHOLD = 12;
const SCROLL_COOLDOWN_MS = 600;

function normalizeWheelDelta(event: WheelEvent): number {
  let { deltaY } = event;
  if (event.deltaMode === 1) deltaY *= 16;
  else if (event.deltaMode === 2) deltaY *= window.innerHeight * 0.85;
  return deltaY;
}

function getOnboardingScrollArea(target: EventTarget | null): HTMLElement | null {
  if (!(target instanceof HTMLElement)) return null;
  const area = target.closest('[data-onboarding-scroll-area]');
  return area instanceof HTMLElement ? area : null;
}

function canScrollInDirection(el: HTMLElement, deltaY: number): boolean {
  if (deltaY > 0) {
    return el.scrollTop + el.clientHeight < el.scrollHeight - 2;
  }
  if (deltaY < 0) {
    return el.scrollTop > 2;
  }
  return false;
}

interface UseOnboardingScrollNavOptions {
  rootRef: RefObject<HTMLElement | null>;
  currentStep: number;
  stepCount: number;
  onGoToStep: (index: number) => void;
  enabled?: boolean;
}

export function useOnboardingScrollNav({
  rootRef,
  currentStep,
  stepCount,
  onGoToStep,
  enabled = true,
}: UseOnboardingScrollNavOptions) {
  const reducedMotion = useReducedMotion();
  const accumRef = useRef(0);
  const cooldownRef = useRef(0);
  const stepRef = useRef(currentStep);
  const stepCountRef = useRef(stepCount);
  const onGoToStepRef = useRef(onGoToStep);

  stepRef.current = currentStep;
  stepCountRef.current = stepCount;
  onGoToStepRef.current = onGoToStep;

  useEffect(() => {
    accumRef.current = 0;
  }, [currentStep]);

  useEffect(() => {
    if (!enabled || stepCount <= 0) return;

    const threshold = reducedMotion ? 36 : SCROLL_ACCUM_THRESHOLD;
    const cooldown = reducedMotion ? 0 : SCROLL_COOLDOWN_MS;

    const onWheel = (event: WheelEvent) => {
      const root = rootRef.current;
      if (!root || !(event.target instanceof Node) || !root.contains(event.target)) {
        return;
      }

      const scrollArea = getOnboardingScrollArea(event.target);
      const delta = normalizeWheelDelta(event);

      if (scrollArea && canScrollInDirection(scrollArea, delta)) {
        return;
      }

      if (Math.abs(delta) < 0.5) return;

      event.preventDefault();
      event.stopPropagation();

      const now = performance.now();
      if (cooldown > 0 && now - cooldownRef.current < cooldown) return;

      accumRef.current += delta;
      if (Math.abs(accumRef.current) < threshold) return;

      const step = stepRef.current;
      const count = stepCountRef.current;
      const nextStep =
        accumRef.current > 0 ? Math.min(step + 1, count - 1) : Math.max(step - 1, 0);

      accumRef.current = 0;

      if (nextStep === step) return;

      cooldownRef.current = now;
      onGoToStepRef.current(nextStep);
    };

    document.addEventListener('wheel', onWheel, { passive: false, capture: true });
    return () => document.removeEventListener('wheel', onWheel, { capture: true });
  }, [enabled, stepCount, reducedMotion, rootRef]);
}
